import React, { useState, useEffect } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { useCreateSkill, useSkill } from '../../apis/skill-registry';
import { Button } from '../../components';

import { useTranslation } from 'react-i18next';

interface ServiceNowSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ServiceNowSkillSetup: React.FC<ServiceNowSkillSetupProps> = ({
  skill,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [domain, setDomain] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: enableSkill, isPending: isEnabling } = useCreateSkill();
  const { data: existingSkill } = useSkill('ServiceNow', skill.enabled);

  // Initialize state with existing configuration
  useEffect(() => {
    if (existingSkill?.config) {
      const config = existingSkill.config;
      if (typeof config.domain === 'string') {
        setDomain(config.domain);
      }
      if (typeof config.username === 'string') {
        setUsername(config.username);
      }
    }
  }, [existingSkill?.config]);

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await enableSkill({
        skillType: 'ServiceNow',
        enabled: true,
        config: {
          domain,
          username,
        },
        ...(password?.trim() && {
          secrets: {
            password,
          },
        }),
      });
      await onComplete();
    } catch (error) {
      console.error('Failed to setup ServiceNow skill:', error);
      setError(t('failedToConfigureServiceNowPleaseCheckIf'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingConfig =
    typeof existingSkill?.config?.domain === 'string' ||
    typeof existingSkill?.config?.username === 'string';
  const isReconfiguring = Boolean(
    existingSkill?.enabled || skill.enabled || hasExistingConfig,
  );

  const isDisabled =
    isLoading ||
    isEnabling ||
    isSubmitting ||
    !domain?.trim() ||
    !username?.trim() ||
    (!isReconfiguring && !password?.trim());

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          ServiceNow
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-blue-50 blur-xl dark:bg-blue-950/10" />
            {skill.image ? (
              <Image
                src={skill.image.src}
                alt={skill.image.alt}
                className="relative h-20 rounded-xl object-cover"
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iHelpYouManageAndTrackIncidents')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/servicenow"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 5. Form Fields */}
        <div className="mb-8 space-y-4">
          <div className="mx-auto max-w-md">
            <label
              htmlFor="domain-url"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('domainUrl')} <span className="text-red-500">*</span>
            </label>
            <input
              id="domain-url"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder={t('enterYourServiceNowDomainUrl')}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600"
              disabled={isLoading || isEnabling || isSubmitting}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('examplePlerionInstanceServiceNowCom')}
            </p>
          </div>

          <div className="mx-auto max-w-md">
            <label
              htmlFor="username"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('userName')} <span className="text-red-500">*</span>
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('enterYourServiceNowUsername')}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600"
              disabled={isLoading || isEnabling || isSubmitting}
            />
          </div>

          <div className="mx-auto max-w-md">
            <label
              htmlFor="password"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('password')} <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('enterYourServiceNowPassword')}
              className="h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600"
              disabled={isLoading || isEnabling || isSubmitting}
            />
            {isReconfiguring && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('thisFieldAppearsEmptyForSecurityReasons')}
              </p>
            )}
          </div>
        </div>

        {/* 6. Call to Action Button */}
        <div className="mb-6 flex flex-col items-center">
          <Button
            onClick={handleSubmit}
            disabled={isDisabled}
            className={`group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all ${
              isDisabled
                ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                : ''
            }`}
          >
            <span>
              {isReconfiguring
                ? t('updateConfiguration')
                : t('activateServiceNow')}
            </span>
          </Button>
        </div>

        {/* 7. Cancel or Go Back Button */}
        <div className="flex flex-col items-center">
          <button
            onClick={onCancel}
            disabled={isLoading || isEnabling || isSubmitting}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            {t('common:cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};
